import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AppToastContext from "./AppToast.context";
import AppToastsContainer from "./AppToastsContainer.component";
import type { AddAppToastInput, AppToastProps } from "./AppToast.types";

const defaultToastDurationMs = 4000;

const AppToastsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [toasts, setToasts] = useState<AppToastProps[]>([]);
  const timeoutsRef = useRef(new Map<number, number>());
  const nextToastIdRef = useRef(0);

  const removeToast = useCallback((id: number) => {
    const timeoutId = timeoutsRef.current.get(id);
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      timeoutsRef.current.delete(id);
    }

    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id),
    );
  }, []);

  const addToast = useCallback(
    (toast: AddAppToastInput) => {
      const nextToast: AppToastProps = {
        durationMs: defaultToastDurationMs,
        ...toast,
        id: nextToastIdRef.current,
      };
      nextToastIdRef.current += 1;

      setToasts((currentToasts) => [nextToast, ...currentToasts]);

      if (nextToast.durationMs > 0) {
        const timeoutId = window.setTimeout(
          () => removeToast(nextToast.id),
          nextToast.durationMs,
        );
        timeoutsRef.current.set(nextToast.id, timeoutId);
      }

      return nextToast.id;
    },
    [removeToast],
  );

  useEffect(
    () => () => {
      timeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      timeoutsRef.current.clear();
    },
    [],
  );

  const value = useMemo(
    () => ({
      addToast,
      removeToast,
    }),
    [addToast, removeToast],
  );

  return (
    <AppToastContext.Provider value={value}>
      {toasts.length > 0 ? (
        <AppToastsContainer toasts={toasts} removeToast={removeToast} />
      ) : null}
      {children}
    </AppToastContext.Provider>
  );
};

export default React.memo(AppToastsProvider);
